import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Shield, Users, Building2, TrendingUp, KeyRound,
  Trash2, UserX, UserCheck, ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  buyer:  { bg: '#DCFCE7', color: '#16A34A' },
  agent:  { bg: '#DBEAFE', color: '#2563EB' },
  seller: { bg: '#FEF3C7', color: '#D97706' },
  admin:  { bg: '#F3E8FF', color: '#9333EA' },
};

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      data-testid={testId}
      className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: color + '15' }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div className="text-3xl font-black" style={{ color: '#0A192F' }}>{value}</div>
      <div className="text-sm text-slate-500 font-medium mt-1">{label}</div>
    </motion.div>
  );
}

function UsersTable({ authFetch }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState('');

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`${API}/api/admin/users`);
    if (r.ok) setUsers(await r.json());
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleActive = async (user) => {
    setActioning(user.id);
    await authFetch(`${API}/api/admin/users/${user.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !user.active }),
    });
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: !u.active } : u));
    setActioning('');
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={22} className="animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="text-left text-xs font-semibold text-slate-500 pb-3 pr-4">User</th>
            <th className="text-left text-xs font-semibold text-slate-500 pb-3 pr-4">Email</th>
            <th className="text-left text-xs font-semibold text-slate-500 pb-3 pr-4">Role</th>
            <th className="text-left text-xs font-semibold text-slate-500 pb-3 pr-4">Joined</th>
            <th className="text-left text-xs font-semibold text-slate-500 pb-3">Status</th>
            <th className="pb-3" />
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const rc = ROLE_COLORS[u.role] || ROLE_COLORS.buyer;
            const isActive = u.active !== false;
            return (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`}
                className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                <td className="py-3 pr-4 font-semibold text-slate-800">{u.name}</td>
                <td className="py-3 pr-4 text-slate-500 text-xs">{u.email}</td>
                <td className="py-3 pr-4">
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
                    style={{ background: rc.bg, color: rc.color }}>{u.role}</span>
                </td>
                <td className="py-3 pr-4 text-slate-400 text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    {isActive ? 'Active' : 'Deactivated'}
                  </span>
                </td>
                <td className="py-3 pl-4">
                  {u.role !== 'admin' && (
                    <button onClick={() => toggleActive(u)} disabled={actioning === u.id}
                      data-testid={`admin-toggle-user-${u.id}`}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all hover:shadow-sm disabled:opacity-50"
                      style={isActive
                        ? { borderColor: '#FCA5A5', color: '#DC2626', background: '#FEF2F2' }
                        : { borderColor: '#A7F3D0', color: '#059669', background: '#ECFDF5' }}>
                      {isActive ? <><UserX size={12} />Deactivate</> : <><UserCheck size={12} />Activate</>}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ListingsTable({ authFetch }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState('');

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`${API}/api/admin/listings`);
    if (r.ok) setListings(await r.json());
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const deactivate = async (id) => {
    if (!window.confirm('Remove this listing from the platform?')) return;
    setActioning(id);
    await authFetch(`${API}/api/admin/listings/${id}`, { method: 'DELETE' });
    setListings(prev => prev.filter(l => l.id !== id));
    setActioning('');
  };

  const STATUS_COLORS = {
    active:         { bg: '#DCFCE7', color: '#16A34A' },
    sold:           { bg: '#FEE2E2', color: '#DC2626' },
    under_contract: { bg: '#FEF3C7', color: '#D97706' },
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={22} className="animate-spin text-slate-400" />
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="text-left text-xs font-semibold text-slate-500 pb-3 pr-4">Address</th>
            <th className="text-left text-xs font-semibold text-slate-500 pb-3 pr-4">City</th>
            <th className="text-left text-xs font-semibold text-slate-500 pb-3 pr-4">Price</th>
            <th className="text-left text-xs font-semibold text-slate-500 pb-3 pr-4">Status</th>
            <th className="text-left text-xs font-semibold text-slate-500 pb-3">Owner</th>
            <th className="pb-3" />
          </tr>
        </thead>
        <tbody>
          {listings.map(l => {
            const sc = STATUS_COLORS[l.status] || STATUS_COLORS.active;
            return (
              <tr key={l.id} data-testid={`admin-listing-row-${l.id}`}
                className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                <td className="py-3 pr-4 font-semibold text-slate-800 max-w-[180px] truncate">{l.address}</td>
                <td className="py-3 pr-4 text-slate-500 text-xs">{l.city}, {l.state}</td>
                <td className="py-3 pr-4 font-bold text-slate-800">${l.price?.toLocaleString()}</td>
                <td className="py-3 pr-4">
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
                    style={{ background: sc.bg, color: sc.color }}>
                    {l.status?.replace('_', ' ')}
                  </span>
                </td>
                <td className="py-3 pr-4 text-slate-400 text-xs capitalize">
                  {l.created_by_role || 'seed'}
                </td>
                <td className="py-3">
                  <button onClick={() => deactivate(l.id)} disabled={actioning === l.id}
                    data-testid={`admin-delete-listing-${l.id}`}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-200 text-red-500 bg-red-50 hover:bg-red-100 transition-all disabled:opacity-50">
                    <Trash2 size={11} />Remove
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
      <button onClick={() => setOpen(p => !p)} data-testid={testId}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors">
        <h3 className="text-base font-bold flex items-center gap-2.5" style={{ color: '#0A192F' }}>
          <Icon size={18} className="text-slate-500" />{title}
        </h3>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-slate-100">
            <div className="p-6">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, authFetch } = useAuth();
  const [stats, setStats] = useState({ buyers: 0, agents: 0, sellers: 0, total_listings: 0, mrr: 0 });

  useEffect(() => {
    authFetch(`${API}/api/users/stats`)
      .then(r => r.ok ? r.json() : {})
      .then(data => setStats(data))
      .catch(() => {});
  }, [authFetch]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Header */}
      <div className="p-5 rounded-2xl flex items-center gap-4"
        style={{ background: 'linear-gradient(135deg, #0A192F 0%, #312e81 100%)' }}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-purple-500">
          <Shield size={18} className="text-white" />
        </div>
        <div>
          <p className="text-white font-bold">Admin Dashboard — {user?.name}</p>
          <p className="text-slate-400 text-xs mt-0.5">Goldielocks Platform Management</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Users}     label="Total Buyers"   value={stats.buyers}                              color="#10B981" testId="admin-stat-buyers" />
        <StatCard icon={KeyRound}  label="Active Sellers" value={stats.sellers || 0}                        color="#F97316" testId="admin-stat-sellers" />
        <StatCard icon={Building2} label="Active Agents"  value={stats.agents}                              color="#0A192F" testId="admin-stat-agents" />
        <StatCard icon={Building2} label="Total Listings" value={stats.total_listings}                      color="#6366F1" testId="admin-stat-listings" />
        <StatCard icon={TrendingUp} label="MRR"           value={`$${stats.mrr.toLocaleString()}`}         color="#F59E0B" testId="admin-stat-mrr" />
      </div>

      {/* User Management */}
      <CollapsibleSection title="User Management" icon={Users} defaultOpen={true} testId="admin-users-section">
        <UsersTable authFetch={authFetch} />
      </CollapsibleSection>

      {/* Listing Management */}
      <CollapsibleSection title="Listing Management" icon={Building2} defaultOpen={false} testId="admin-listings-section">
        <ListingsTable authFetch={authFetch} />
      </CollapsibleSection>
    </div>
  );
}
